import { useMemo, useState } from 'react';
import { StepTwoProps, Steps } from '../../types';
import { Field } from '@/isomorphic/components/forms/components/field';
import { Checkbox } from '@/isomorphic/components/forms/components/checkbox';
import { Button } from '@/isomorphic/components/typography/button/button';
import { AccountFields, validateAccount, validateClearing } from '../account-fields';
import { Arrow } from '../svg';
import clsx from 'clsx';
import { useBrandedColors } from '../../hooks/use-branded-colors';

export const StepTwo = ({ step, setStep, fields, updateFields, getLabel, insuranceLinks, brandColor, scrollToTop }: StepTwoProps) => {
    const { payoutAccount } = fields;
    const { useBrandedColorsBool, getStyle } = useBrandedColors({ brandColor });

    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const numberValidator = (min: number, max: number) => {
        return (value: string) => {
            if (!value || value.trim() === '') {
                return false;
            }

            const numberValue = Number(value);

            if (isNaN(numberValue)) {
                return false;
            }

            return numberValue >= min && numberValue <= max;
        };
    };

    const validationState = useMemo(() => {
        const requiredFields = {
            montlyIncomeGross: numberValidator(0, 999999)(fields.montlyIncomeGross),
            livingExpenses: numberValidator(0, 99999)(fields.livingExpenses),
            otherLoanExpenses: numberValidator(0, 500000)(fields.otherLoanExpenses),
            clearingNumber: fields.payoutAccount.clearingNumber && validateClearing(fields.payoutAccount.clearingNumber),
            accountNumber: fields.payoutAccount.accountNumber && validateAccount(fields.payoutAccount.accountNumber),
        };

        return {
            ...requiredFields,
            allFieldsValid: Object.values(requiredFields).every(Boolean),
        };
    }, [fields]);

    const handleNext = () => {
        if (validationState.allFieldsValid) {
            setStep((step + 1) as Steps);
            return;
        }

        scrollToTop();
        setShowValidationErrors(true);
    };

    return (
        <div className="r-relative r-mx-auto r-max-w-2xl r-px-4">
            <div className="r-flex r-flex-col">
                <div className="r-mb-4">
                    <h4 className={clsx('r-pb-4 r-text-xl r-font-semibold', useBrandedColorsBool ? 'r-text-black' : 'r-text-primary-500')}>
                        {getLabel('householdEconomyTitle')}
                    </h4>
                    <Field
                        required
                        grouped="top"
                        value={fields.montlyIncomeGross}
                        label={getLabel('monthlyIncomeGross')}
                        validation={numberValidator(0, 999999)}
                        infoText={getLabel('monthlyIncomeGrossInfoText')}
                        onChange={value => updateFields({ montlyIncomeGross: value })}
                        showRequiredError={showValidationErrors && !validationState.montlyIncomeGross}
                    />
                    <Field
                        required
                        grouped="middle"
                        value={fields.livingExpenses}
                        label={getLabel('livingExpenses')}
                        validation={numberValidator(0, 99999)}
                        infoText={getLabel('livingExpensesInfoText')}
                        onChange={value => updateFields({ livingExpenses: value })}
                        showRequiredError={showValidationErrors && !validationState.livingExpenses}
                    />
                    <Field
                        required
                        grouped="bottom"
                        value={fields.otherLoanExpenses}
                        label={getLabel('otherLoanExpenses')}
                        validation={numberValidator(0, 500000)}
                        infoText={getLabel('otherLoanExpensesInfoText')}
                        onChange={value => updateFields({ otherLoanExpenses: value })}
                        showRequiredError={showValidationErrors && !validationState.otherLoanExpenses}
                    />
                </div>
                <div className="r-space-y-2 r-pb-4 r-pt-4">
                    <h4 className={clsx('r-text-xl r-font-semibold', useBrandedColorsBool ? 'r-text-black' : 'r-text-primary-500')}>
                        {getLabel('paymentAccountTitle')}
                    </h4>
                    <p>{getLabel('paymentAccountInfo')}</p>
                </div>
                <AccountFields
                    getLabel={getLabel}
                    values={{ ...payoutAccount }}
                    showValidationErrors={showValidationErrors}
                    setValues={values => updateFields({ payoutAccount: { ...fields.payoutAccount, ...values } })}
                />
                <div className="r-space-y-4">
                    <div className="r-pt-8">
                        <h4 className={clsx('r-pb-2 r-text-xl r-font-semibold', useBrandedColorsBool ? 'r-text-black' : 'r-text-primary-500')}>
                            {getLabel('insuranceTitle')}
                        </h4>
                        <p>{getLabel('insuranceInfoText')}</p>
                    </div>
                    <Checkbox
                        type="checkbox"
                        label={getLabel('insuranceApproveText')}
                        checked={fields.insurance === true}
                        onChange={checked => updateFields({ insurance: checked })}
                        brandColor={brandColor}
                    />
                    <div
                        className={clsx('styled-link', useBrandedColorsBool ? 'brand-styles' : '')}
                        dangerouslySetInnerHTML={{ __html: getLabel('insuranceApproveInfoText') }}
                        style={useBrandedColorsBool ? getStyle() : {}}
                    />
                    <div
                        className={clsx('styled-link r-flex r-flex-col r-gap-2 r-pb-4', useBrandedColorsBool ? 'brand-styles' : '')}
                        style={useBrandedColorsBool ? getStyle() : {}}
                    >
                        {insuranceLinks && insuranceLinks.length > 0 && (
                            <div>
                                <h4
                                    className={clsx('r-mb-2 r-text-lg r-font-semibold', useBrandedColorsBool ? 'r-text-black' : 'r-text-primary-500')}
                                >
                                    {getLabel('insuranceLinksTitle')}
                                </h4>
                                <div className="r-flex r-flex-col r-gap-2">
                                    {insuranceLinks.map((link, index) => (
                                        <a key={index} href={link.url} target="_blank" rel="noopener noreferrer">
                                            {link.title}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="r-flex r-gap-4 r-pt-8 max-lg:r-flex-col">
                <Button brandColor={brandColor} type="secondary" fullWidth onClick={() => setStep((step - 1) as Steps)}>
                    <div className="r-flex r-gap-2">
                        <Arrow className="r-w-4 r-rotate-180" />
                        <p>{getLabel('backButton')}</p>
                    </div>
                </Button>
                <Button brandColor={brandColor} fullWidth ariaDisabled={!validationState.allFieldsValid} onClick={handleNext}>
                    <div className="r-flex r-gap-2">
                        <p>{getLabel('continueButton')}</p>
                        <Arrow className="r-w-4" />
                    </div>
                </Button>
            </div>
        </div>
    );
};
